import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { InternalGrid, ThemeDefault } from '@paygreen/paygreen-ui';
import { CardClassic, ErrorBoundary, H1Title } from '../../../components';
import { IsDesktop } from '../../../utils/tools';
import { useNodeDimensions } from '../../../utils/hooks';
import { useUser, useCharityList } from '../../../context';
import { CharityMessage } from '../../../components';
import UserCompanyBillingAddress from '../company/UserCompanyBillingAddress';
import UserCharityInfo from './UserCharityInfo';
import CharityLogoCard from './CharityLogoCard';

const UserCharity = ({ t }) => {
    const { user } = useUser();
    const { charityListData, setCharityListData } = useCharityList();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    // we create a ref using a special hook to get width from main Grid component
    const [ref, { width }] = useNodeDimensions();

    const [isLoading, setIsLoading] = useState(true);
    const [charityDataLocal, setCharityDataLocal] = useState({});

    useEffect(() => {
        isMounted.current = true;

        if (user?.role === 'ASSOCIATION' && charityListData?.[0]) {
            let tempRes = charityListData[0];

            if (isMounted.current) {
                setCharityDataLocal({
                    ...tempRes,
                    fieldOfAction: t(
                        `charity.edit.fieldOfActionValues.${charityListData[0].fieldOfAction}`,
                    ),
                });
                // we simulate minimal loading for better UX and synchronization with user form loading
                setTimeout(() => setIsLoading(false), 500);
            }
        }

        return () => {
            isMounted.current = false;
        };
    }, [user, t, charityListData]);

    return (
        <>
            <Helmet>
                <title>
                    {t('charity.see.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('charity.see.title')} />

            <div ref={ref}>
                <InternalGrid
                    displayType="grid"
                    gridGap="lg"
                    gridTemplateColumns={
                        width > ThemeDefault.breakpoint.md ? '1fr 1fr' : '1fr'
                    }
                    justifyItems="stretch"
                    hasStaticWidth={!IsDesktop()}
                    childrenMarginTop="md"
                >
                    <ErrorBoundary>
                        <UserCharityInfo
                            charityDataLocal={charityDataLocal}
                            isLoading={isLoading}
                            setCharityListData={setCharityListData}
                        />
                    </ErrorBoundary>

                    <InternalGrid
                        displayType="grid"
                        gridGap="lg"
                        justifyItems="stretch"
                    >
                        <CharityMessage />

                        <ErrorBoundary>
                            <CharityLogoCard
                                charityDataLocal={charityDataLocal}
                                charityListData={charityListData}
                                setCharityListData={setCharityListData}
                                isLoading={isLoading}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary>
                            <CardClassic
                                title={t('charity.see.adressTitle')}
                                titleSize="md"
                                isShadowWab
                                blockWidth="md"
                            >
                                <UserCompanyBillingAddress
                                    account={user.accountId}
                                    isLoading={isLoading}
                                />
                            </CardClassic>
                        </ErrorBoundary>
                    </InternalGrid>
                </InternalGrid>
            </div>
        </>
    );
};

export default withTranslation('common')(UserCharity);
